import { useState, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { motion, AnimatePresence } from 'framer-motion'
import { TextArea } from '../ui/TextArea'
import { uploadPhoto } from '../../lib/api'
import type { FormData } from './FormWizard'

interface UploadedPhoto {
  key: string
  preview: string
  name: string
}

export function StepPhotos() {
  const { register, setValue, formState: { errors } } = useFormContext<FormData>()
  const [photos, setPhotos] = useState<UploadedPhoto[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    setUploadError(null)

    const newPhotos: UploadedPhoto[] = []

    for (const file of Array.from(files)) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setUploadError('Please upload only image files')
        continue
      }

      // Validate file size (10MB)
      if (file.size > 10 * 1024 * 1024) {
        setUploadError('File too large. Maximum size is 10MB')
        continue
      }

      const result = await uploadPhoto(file)
      
      if (result.success && result.key) {
        newPhotos.push({
          key: result.key,
          preview: URL.createObjectURL(file),
          name: file.name,
        })
      } else {
        setUploadError(result.error || 'Upload failed')
      }
    }

    if (newPhotos.length > 0) {
      const updated = [...photos, ...newPhotos]
      setPhotos(updated)
      setValue('photos', updated.map(p => p.key))
    }

    setUploading(false)
    e.target.value = '' // Reset input
  }, [photos, setValue])

  const removePhoto = useCallback((key: string) => {
    const updated = photos.filter(p => p.key !== key)
    setPhotos(updated)
    setValue('photos', updated.map(p => p.key))
  }, [photos, setValue])

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Almost done!
        </h2>
        <p className="text-gray-600">
          Add photos and any additional details (optional)
        </p>
      </div>

      {/* Photo Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Photos of your aquarium or space
        </label>
        
        <div className="relative">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            disabled={uploading}
            className="sr-only"
            id="photo-upload"
          />
          <label
            htmlFor="photo-upload"
            className={`
              flex flex-col items-center justify-center w-full h-32 
              border-2 border-dashed rounded-xl cursor-pointer
              transition-all duration-200
              ${uploading 
                ? 'border-gray-300 bg-gray-50 cursor-wait' 
                : 'border-gray-300 hover:border-aquatic-400 hover:bg-aquatic-50'
              }
            `}
          >
            {uploading ? (
              <div className="flex items-center space-x-2 text-gray-500">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>Uploading...</span>
              </div>
            ) : (
              <>
                <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm text-gray-600">
                  Click to upload photos
                </span>
                <span className="text-xs text-gray-400 mt-1">
                  JPEG, PNG, WebP up to 10MB each
                </span>
              </>
            )}
          </label>
        </div>

        {uploadError && (
          <p className="mt-2 text-sm text-red-600">{uploadError}</p>
        )}

        {/* Photo Previews */}
        <AnimatePresence>
          {photos.length > 0 && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4 grid grid-cols-3 gap-3"
            >
              {photos.map((photo) => (
                <motion.div
                  key={photo.key}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="relative group"
                >
                  <img
                    src={photo.preview}
                    alt={photo.name}
                    className="w-full h-24 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => removePhoto(photo.key)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full 
                             flex items-center justify-center opacity-0 group-hover:opacity-100 
                             transition-opacity shadow-lg"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Additional Notes */}
      <TextArea
        {...register('notes')}
        label="Additional Notes"
        placeholder="Tell us anything else that would help us understand your project better..."
        error={errors.notes?.message}
        hint="Describe your vision, concerns, or questions"
      />
    </div>
  )
}
